import { NormalizedAction, NormalizedKeyword } from '@/types/action-types';
import { groupKeywordsByAd } from './normalize-action';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { Eye } from 'lucide-react';

// Removed unused function processAdGroupedActions

interface KeywordListProps {
  action: NormalizedAction;
  selectedKeywordId?: number;
  onSelectKeyword?: (keywordId: number) => void;
  onViewDetails?: (keywordId: number) => void;
  isModal?: boolean;
}

export function KeywordList({
  action,
  selectedKeywordId,
  onSelectKeyword,
  onViewDetails,
  isModal = false,
}: KeywordListProps) {
  // No keyword filtering in this version - show all keywords
  const keywords = action.keywords || [];

  // Group keywords by ad for display
  const keywordsByAd = groupKeywordsByAd(keywords);

  // Generate title based on action type
  const getTitle = () => {
    switch (action.actionType) {
      case 'ADD_KEYWORDS':
        return 'Added Keywords';
      case 'ADD_EXACT_MATCH_KEYWORD':
        return 'Added Exact Match Keywords';
      case 'PAUSE_KEYWORDS':
        return 'Paused Keywords';
      case 'DELETE_KEYWORDS':
        return 'Removed Keywords';
      case 'ADD_NEGATIVE_KEYWORD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_AD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN':
        return 'Added Negative Keywords';
      default:
        return 'Keywords';
    }
  };

  // Generate reason message
  const getMessage = () => {
    if (action.reason) {
      switch (action.actionType) {
        case 'ADD_KEYWORDS':
          return `These keywords were added because ${action.reason.toLowerCase()}.`;
        case 'ADD_EXACT_MATCH_KEYWORD':
          return `These exact match keywords were added because ${action.reason.toLowerCase()}.`;
        case 'PAUSE_KEYWORDS':
          return `These keywords were paused because ${action.reason.toLowerCase()}.`;
        case 'DELETE_KEYWORDS':
          return `These keywords were removed because ${action.reason.toLowerCase()}.`;
        case 'ADD_NEGATIVE_KEYWORD':
        case 'ADD_NEGATIVE_KEYWORD_FOR_AD':
        case 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN':
          return `These negative keywords were added because ${action.reason.toLowerCase()}.`;
        default:
          return `These keywords were modified because ${action.reason.toLowerCase()}.`;
      }
    }

    // Default messages
    switch (action.actionType) {
      case 'ADD_KEYWORDS':
        return 'These keywords were added to improve campaign performance.';
      case 'ADD_EXACT_MATCH_KEYWORD':
        return 'These exact match keywords were added to improve campaign targeting.';
      case 'PAUSE_KEYWORDS':
        return 'These keywords were paused due to underperformance.';
      case 'DELETE_KEYWORDS':
        return 'These keywords were removed due to performance issues.';
      case 'ADD_NEGATIVE_KEYWORD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_AD':
      case 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN':
        return 'These negative keywords were added to prevent unwanted traffic.';
      default:
        return 'These keywords were modified to optimize campaign performance.';
    }
  };

  return (
    <div className={`overflow-y-auto ${isModal ? '' : 'bg-white h-full'}`}>
      <div className='p-6'>
        <h3 className='text-lg font-semibold mb-2'>
          {action.isUndone ? 'Reverted: ' : ''}{getTitle()}
        </h3>
        <p className='text-sm text-muted-foreground mb-6'>{getMessage()}</p>

        {Object.keys(keywordsByAd).length > 0 ? (
          <div className='space-y-6'>
            {Object.entries(keywordsByAd).map(([adId, adData]) => (
              <div key={adId} className='space-y-2'>
                <h4 className='text-sm font-medium mb-2'>
                  <span className='text-blue-600'>
                    {adData.internalUrl ? (
                      <Link
                        to={adData.internalUrl}
                        className="hover:underline"
                      >
                        {adData.adUrl && adData.adUrl.length > 40 ? `${adData.adUrl.substring(0, 40)}...` : adData.adUrl || adData.adName}
                      </Link>
                    ) : (
                      adData.adName
                    )}
                  </span>
                </h4>
                <div className='space-y-2'>
                  {adData.keywords.map((keyword) => (
                    <KeywordItem
                      key={keyword.id}
                      keyword={keyword}
                      isSelected={selectedKeywordId === keyword.id}
                      onSelect={() => {
                        onSelectKeyword?.(keyword.id);
                      }}
                      onViewDetails={onViewDetails ? () => {
                        onViewDetails(keyword.id);
                      } : undefined}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-center py-8'>
            <p className='text-sm text-muted-foreground'>No keywords found for this action.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual keyword item - used in both list view and modal
 */
function KeywordItem({
  keyword,
  isSelected,
  onSelect,
  onViewDetails,
}: {
  keyword: NormalizedKeyword;
  isSelected: boolean;
  onSelect: () => void;
  onViewDetails?: () => void;
}) {
  return (
    <div
      className={`p-3 rounded text-sm flex justify-between items-center group
                 ${isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50' : 'bg-muted'}`}
      onClick={onSelect}
    >
      <div className='flex items-center'>
        <span>{keyword.text}</span>
        {keyword.matchType && (
          <Badge variant='outline' className='ml-2 text-xs'>
            {keyword.matchType}
          </Badge>
        )}
      </div>

      <div className='flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
        {/* Only show details button in list view, not in modal */}
        {onViewDetails && (
          <Button
            size='sm'
            variant='ghost'
            className='h-5 px-1 flex items-center gap-1'
            onClick={(e) => {
              e.stopPropagation(); // Prevent selection
              onViewDetails();
            }}
          >
            <Eye className='h-3 w-3 text-muted-foreground' />
            <span className='text-xs text-muted-foreground'>Details</span>
          </Button>
        )}
      </div>
    </div>
  );
}
